import { useEffect, useState } from "react";
import { ArrowRight, Check, Monitor, PlayCircle, ShieldCheck } from "lucide-react";
import { TRIAL_DAYS, TRIAL_PLAN_NAME } from "@omnicanvas/core";
import { ActionLink, SectionLabel } from "@omnicanvas/ui";

type Platform = "windows" | "other";

function detectPlatform(): Platform {
  return /windows/i.test(navigator.userAgent) ? "windows" : "other";
}

export function Hero() {
  const [platform, setPlatform] = useState<Platform>("other");

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  return (
    <section className="relative overflow-hidden border-b border-slate-100 bg-slate-50 pt-28 pb-20 md:pt-36 md:pb-28">
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-indigo-500/30" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-[0.86fr_1.14fr] lg:gap-16">
        <div>
          <SectionLabel className="mb-5 text-indigo-600">
            A note-taking system for visual follow-through
          </SectionLabel>
          <h1 className="max-w-xl text-5xl font-extrabold leading-[1.04] tracking-[-0.045em] text-slate-950 md:text-6xl">
            Capture anything. Turn it into what happens next.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
            Bring in a handwritten page, a sketch, a PDF, a web clip—or record a
            meeting. Turn handwriting into text you can search, clarify what
            matters, and review it later—without forcing every kind of thinking
            into the same shape.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            {/*
              The primary button used to open a sample meeting. Meeting queries
              drew 2,099 impressions and one click, and four accounts have ever
              transcribed, so the homepage was leading on the job nobody arrives
              for. A populated canvas opens instead — still a real workspace
              rather than the blank page every canvas tool hands you, which is
              why the old blank-canvas button is gone rather than promoted.
              The meeting sample keeps a place, just not the first one.
            */}
            <ActionLink
              href="https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=marketing&utm_medium=hero&utm_campaign=live_canvas"
              size="lg"
              className="group"
            >
              Try a live canvas — no account
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
            </ActionLink>
            <a
              href="https://app.omnicanvasnotes.com/?mode=try&action=sample-meeting&utm_source=marketing&utm_medium=hero&utm_campaign=interactive_sample"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-indigo-600"
            >
              <PlayCircle size={16} />
              Or try a sample meeting
            </a>
          </div>

          <p className="mt-6 flex items-start gap-2.5 text-base font-semibold leading-7 text-slate-900">
            <ShieldCheck size={19} className="mt-0.5 flex-shrink-0 text-emerald-600" />
            <span>
              No bot joins your call.{" "}
              <span className="font-normal text-slate-600">
                OmniCanvas captures audio from your own device, with a consent
                reminder before you record so everyone can be told up front.
              </span>
            </span>
          </p>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-slate-500">
            {["No account", "Core canvas stays free", `${TRIAL_PLAN_NAME} free for ${TRIAL_DAYS} days, no card`].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5">
                <Check size={13} className="text-emerald-600" />
                {item}
              </span>
            ))}
          </div>

          <div className="mt-8 grid max-w-xl grid-cols-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {["Capture", "Clarify", "Act", "Review"].map((stage, index) => (
              <div key={stage} className={`relative px-2 py-3 text-center text-[10px] font-extrabold uppercase tracking-[0.12em] ${
                index === 3 ? "text-emerald-700" : "text-slate-600"
              }`}>
                {index > 0 && <span aria-hidden="true" className="absolute inset-y-2 left-0 w-px bg-slate-200" />}
                <span className="mr-1 text-slate-300">{index + 1}</span>
                {stage}
              </div>
            ))}
          </div>

          {platform === "windows" && (
            <a
              href="https://app.omnicanvasnotes.com/api/downloads/windows-latest.exe"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-indigo-600"
            >
              <Monitor size={15} />
              Prefer a native app? Download for Windows
            </a>
          )}
        </div>

        <div className="relative lg:-mr-16">
          <div aria-hidden="true" className="absolute -inset-6 -z-10 rounded-[2rem] bg-indigo-100/55 blur-2xl" />
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_32px_80px_-36px_rgba(15,23,42,0.42)]">
            <img
              src="/images/hero-workspace.webp"
              width={2000}
              height={1250}
              // React 18 does not know fetchPriority; pass the HTML attribute as-is.
              {...{ fetchpriority: "high" }}
              decoding="async"
              alt="The OmniCanvas workspace: a product launch brief with a launch narrative, a customer call recording and its transcript, a Next actions checklist with owners, and sticky notes, connected by arrows."
              className="h-auto w-full"
            />
          </div>
          <div className="mt-3 flex items-center justify-between px-1 text-[11px] text-slate-500">
            <span>A real OmniCanvas workspace</span>
            <a href="#meeting-workflow" className="font-semibold text-indigo-600 hover:text-indigo-700">
              See the before and after
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
